import {
    ActivityModality,
    CatalogItem,
    CourseOutline,
    DeepRequired,
    DeliverySession,
    LearningActivity,
    LearningActivityStatus,
    LmsType,
    WaitlistEnabled,
} from './types';
import { v4 as uuid } from 'uuid';
import { TEST_ORGANIZATION_ID, TEST_TEAM_ID } from '../testUtils/constants';
import { FeatureFlagsCondition } from './actionButtonRules/conditions';

export const buildLearningActivity = (params?: Record<string, unknown>): LearningActivity => {
    const catalogItem = mockCatalogItem();
    return {
        pk: (params?.pk as string) ?? '12345',
        activity_status: LearningActivityStatus.ACTIVE,
        activity_modality: (params?.activity_modality as ActivityModality) ?? ActivityModality.vILT,
        activity_audience: (params?.activity_audience as string) ?? 'Commercial Private',
        class_size: (params?.class_size as number) ?? 5,
        classrooms_student_url: 'https://us-east-1.student.classrooms.aws.training',
        course_name: catalogItem.name,
        delivery_language: 'English',
        delivery_sessions: (params?.delivery_sessions as DeliverySession[]) ?? [
            {
                start_timestamp: 1,
                end_timestamp: 2,
            },
        ],
        customers: [
            {
                organization: {
                    id: TEST_ORGANIZATION_ID,
                },
                team: {
                    id: TEST_TEAM_ID,
                },
                number_of_students_committed: 10,
            },
        ],
        delivery_address_1: (params?.delivery_address_1 as string) ?? '',
        delivery_address_2: (params?.delivery_address_2 as string) ?? '',
        room: (params?.room as string) ?? '',
        delivery_city: (params?.delivery_city as string) ?? '',
        delivery_state: (params?.delivery_state as string) ?? '',
        delivery_postal_code: (params?.delivery_postal_code as string) ?? '',
        delivery_country: (params?.delivery_country as string) ?? '',
        delivery_timezone: (params?.delivery_timezone as string) ?? 'America/Los_Angeles',
        lms_type: LmsType.myClass,
        registrationCounter: (params?.registrationCounter as number) ?? 0,
        v_ilt_id: 'https://prod.evcsp.aurous.training.aws.dev/',
        v_ilt_type: 'WebEx',
        waitlist_enabled: WaitlistEnabled.DISABLED,
        course_catalog_item: params?.hasOwnProperty('course_catalog_item') ? (params.course_catalog_item as CatalogItem) : catalogItem,
        learning_goal_ids: params?.learning_goal_ids as string[] | undefined,
    };
};

// API format mock outline (as returned by CatalogService)
const MOCK_COURSE_OUTLINE_API = [
    {
        id: '1',
        name: 'Course outline',
        children: [
            {
                id: '1',
                name: 'Day 1',
                children: [
                    {
                        id: '1',
                        name: 'Module 1: Introduction to Amazon Web Services',
                        children: [{ id: '1', name: 'Summarize the benefits of AWS', children: [] }],
                    },
                    {
                        id: '2',
                        name: 'Module 2: Compute in the Cloud',
                        children: [{ id: '1', name: 'Describe the benefits of Amazon Elastic Compute Cloud', children: [] }],
                    },
                ],
            },
        ],
    },
];

// Internal format mock outline (what the UI expects)
export const MOCK_COURSE_OUTLINE: CourseOutline = {
    nodes: [
        {
            header: 'Day 1',
            nodes: [
                {
                    header: 'Module 1: Introduction to Amazon Web Services',
                    details: ['Summarize the benefits of AWS'],
                },
                {
                    header: 'Module 2: Compute in the Cloud',
                    details: ['Describe the benefits of Amazon Elastic Compute Cloud'],
                },
            ],
        },
    ],
};

export const mockCatalogItem = (params?: Record<string, unknown>): DeepRequired<CatalogItem> => {
    return {
        id: (params?.id as string) ?? 'catalogItem',
        versionedId: (params?.versionedId as string) ?? 'catalogItem:001:001:001',
        name: (params?.name as string) ?? 'Some Catalog Title',
        description: (params?.description as string) ?? 'Description',
        duration: {
            unit: 'DAYS',
            value: 1,
        },
        domain: {
            valueDisplayString: (params?.domain as string) ?? 'Cloud Essentials',
        },
        skillLevel: {
            valueDisplayString: (params?.skillLevel as string) ?? 'Fundamental',
        },
        objectives: {
            values: (params?.objectives as string[]) ?? ['Summarize the working definition of AWS', 'Differentiate between on-premises'],
        },
        intendedAudience: {
            values: (params?.intendedAudience as string[]) ?? ['Sales', 'Legal', 'Marketing', 'Business analysts and Project managers'],
        },
        prerequisites: {
            value: (params?.intendedAudience as string) ?? '-General IT business knowledge\n- General IT technical knowledge',
        },
        outline: JSON.stringify(MOCK_COURSE_OUTLINE_API),
        children: {
            nodes: [],
        },
    };
};

function getFutureDate(): Date {
    const today = new Date(); // Get the current date and time
    const futureDate = new Date(today); // Create a new Date object to avoid modifying 'today' directly
    futureDate.setDate(today.getDate() + 7); // Add 7 days to current date
    return futureDate;
}

export const MOCK_PARTNER_PUBLIC_UNLISTED_LEARNING_ACTIVITIES = [
    buildLearningActivity({
        id: '1',
        pk: 'test-learning-activity-id',
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 1',
        description: 'Mock Description 1',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1704067200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 1,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 1',
        }),
    }),
    buildLearningActivity({
        id: '2',
        pk: uuid(),
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 2',
        description: 'Mock Description 2',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1704067200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 3,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 2',
        }),
    }),
    buildLearningActivity({
        id: '3',
        pk: uuid(),
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 3',
        description: 'Mock Description 3',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1704067200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 5,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 3',
        }),
    }),
    buildLearningActivity({
        id: '4',
        pk: uuid(),
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 4',
        description: 'Mock Description 4',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1704067200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 7,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 4',
        }),
    }),
    buildLearningActivity({
        id: '5',
        pk: uuid(),
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 5',
        description: 'Mock Description 5',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1704067200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 9,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 5',
        }),
    }),
    buildLearningActivity({
        id: '6',
        pk: 'fixed-test-pk',
        activityStatus: LearningActivityStatus.ACTIVE,
        name: 'Mock Partner Public Unlisted Learning Activity 6 (Fixed ID - Not Started)',
        description: 'Mock Description 5',
        delivery_language: 'en',
        delivery_timezone: 'Europe/London',
        delivery_sessions: [
            {
                start_timestamp: 1893499200,
                end_timestamp: getFutureDate(),
            },
        ],
        class_size: 10,
        registrationCounter: 9,
        course_catalog_item: mockCatalogItem({
            name: 'Mock Partner Public Unlisted Learning Activity 6 (Fixed ID - Not Started)',
        }),
    }),
];

export const mockFeatures: FeatureFlagsCondition = {
    hideJoinButtonForNonWebExCourse: true,
    isTeamAuthorizationEnabled: true,
    privateLinkSharing: true,
    adcLinkSharing: true,
};
